import { okSchema } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Copy, Gamepad2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Confirm } from '@/components/confirm';
import { DuplicateProfileDialog, GameProfileEditor } from '@/components/game-profile-editor';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Button } from '@/components/ui/button';
import { apiRequest } from '@/lib/api';
import { gameProfileQuery } from '@/lib/admin-queries';
import { errorMessage } from '@/lib/forms';
import { profileJson } from '@/lib/profile-json';

export const Route = createFileRoute('/_app/admin/game-profiles/$profileId')({
  component: GameProfilePage,
});

function GameProfilePage() {
  const id = Number(Route.useParams().profileId);
  const query = useQuery(gameProfileQuery(id));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [duplicating, setDuplicating] = useState(false);
  const remove = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/game-profiles/${id}`, { method: 'DELETE', schema: okSchema }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'game-profiles'] });
      await navigate({ to: '/admin/game-profiles' });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/admin/game-profiles">
          <ArrowLeft /> Game profiles
        </Link>
      </Button>
      <QueryState query={query}>
        {(profile) => (
          <>
            <PageHeader
              title={profile.name}
              icon={Gamepad2}
              description={`${profile.source === 'builtin' ? 'Built-in' : 'Custom'} · ${profile.slug} · ${profile.enabled ? `used by ${profile.eggCount} egg${profile.eggCount === 1 ? '' : 's'}` : 'disabled'}`}
              actions={
                <>
                  <Button variant="outline" onClick={() => setDuplicating(true)}>
                    <Copy /> Duplicate
                  </Button>
                  {profile.source === 'custom' && (
                    <Confirm
                      title={`Delete ${profile.name}?`}
                      description="Eggs that match it go back to the other profiles. Profiles pinned to an egg can't be deleted."
                      action="Delete"
                      destructive
                      onConfirm={() => remove.mutate()}
                    >
                      <Button variant="outline" className="text-destructive">
                        <Trash2 /> Delete
                      </Button>
                    </Confirm>
                  )}
                </>
              }
            />
            <GameProfileEditor
              key={`${profile.id}-${profile.updatedAt}`}
              profile={profile}
              initial={profileJson(profile)}
            />
            {duplicating && (
              <DuplicateProfileDialog profile={profile} onClose={() => setDuplicating(false)} />
            )}
          </>
        )}
      </QueryState>
    </>
  );
}
